import type { ReactNode, FC } from "react";
import { createContext, useContext, useMemo, useCallback } from "react";

import { colorsSets } from "@similarweb/styles";

import type { ICategory, ICustomCategory } from "app/common/services/categoryService.types";

import { MAX_COMPARISON_CATEGORIES } from "../routes/routes";

export const CATEGORIES_COMPARISON_COLORS = [colorsSets.c["midnight 300"], colorsSets.c["torquoise 300"]];

// Utility function for color assignment
const getCategoryColor = (categoryIndex: number): string => {
    return CATEGORIES_COMPARISON_COLORS[categoryIndex % CATEGORIES_COMPARISON_COLORS.length];
};

// Types
export interface ICategoryInfo {
    id: string;
    name: string;
    hasSegments?: boolean;
    isCustom?: boolean;
}

export interface ICategoryComparison {
    id: string;
    category: ICategory | ICustomCategory;
    color: string;
}

interface ICategoriesContextValue {
    categories: ICategoryComparison[];
    getPrimaryCategory: () => ICategory | ICustomCategory | null;
    getCategoryById: (id: string) => ICategoryComparison | undefined;
}

export const CategoriesContext = createContext<ICategoriesContextValue | undefined>(undefined);

interface ICategoriesProviderProps {
    children: ReactNode;
    categories?: ICategory[];
}

export const CategoriesProvider: FC<ICategoriesProviderProps> = ({ children, categories = [] }) => {
    // Pure transformation of URL categories into display format
    const categoriesWithColors = useMemo(
        () =>
            categories.map((category, index) => ({
                id: category.forUrl || category.categoryId || category.id,
                category,
                color: getCategoryColor(index),
            })),
        [categories],
    );

    const getPrimaryCategory = useCallback(() => {
        return categoriesWithColors[0]?.category || null;
    }, [categoriesWithColors]);

    const getCategoryById = useCallback(
        (id: string) => {
            return categoriesWithColors.find((item) => item.id === id);
        },
        [categoriesWithColors],
    );

    const contextValue = useMemo(
        () => ({
            categories: categoriesWithColors,
            getPrimaryCategory,
            getCategoryById,
        }),
        [categoriesWithColors, getPrimaryCategory, getCategoryById],
    );

    return <CategoriesContext.Provider value={contextValue}>{children}</CategoriesContext.Provider>;
};

export const useCategories = (): ICategoriesContextValue => {
    const context = useContext(CategoriesContext);
    if (context === undefined) {
        throw new Error("useCategories must be used within a CategoriesProvider");
    }
    return context;
};

/**
 * Hook that returns comparison-related state and helpers
 * Determines if we're in comparison mode and provides comparison utilities
 */
export const useCategoriesComparison = () => {
    const context = useCategories();

    const hasMultipleCategories = context.categories.length > 1;
    const canAddMoreCategories = context.categories.length < MAX_COMPARISON_CATEGORIES;

    const isComparisonMode = hasMultipleCategories;
    const isPrimaryMode = !hasMultipleCategories;

    // Get primary category for single-category mode
    const primaryCategory = context.getPrimaryCategory();

    // Check if any selected category has segments
    const hasSelectedCategoryWithSegments = context.categories.some((categoryComparison) => {
        const category = categoryComparison.category;
        return !!category?.segments?.length;
    });

    return {
        // State
        categories: context.categories,
        primaryCategory,
        hasMultipleCategories,
        canAddMoreCategories,
        isComparisonMode,
        isPrimaryMode,

        // Computed state
        isMaxCategoriesReached: context.categories.length >= MAX_COMPARISON_CATEGORIES,
        hasSelectedCategoryWithSegments,

        // Helpers
        getCategoryById: context.getCategoryById,
    };
};
